import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import TestimonialCarousel from '../components/TestimonialCarousel';
import AnimatedCounter from '../components/AnimatedCounter';
import ParticleBackground from '../components/ParticleBackground';
import ConsultationModal from '../components/ConsultationModal';

const PurchaseModal = ({ isOpen, onClose, service }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    county: '',
    paymentMethod: 'card'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.county) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in all required fields.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: 'Please enter a valid email address.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate payment processing
    setTimeout(() => {
      setIsSubmitting(false);
      Swal.fire({
        icon: 'success',
        title: 'Purchase Successful!',
        text: `Thank you for your purchase! You will receive access to "${service.title}" within 24 hours. A confirmation email has been sent to ${formData.email}.`,
        confirmButtonColor: '#2563eb'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        county: '',
        paymentMethod: 'card'
      });
      onClose();
    }, 3000);
  };

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in max-h-[95vh]">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-secondary-500 hover:text-secondary-800 bg-white/90 hover:bg-white rounded-full p-2 shadow-sm transition-colors duration-200 z-20"
          aria-label="Close purchase modal"
        >
          <i className="bi bi-x-lg text-lg"></i>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 max-h-full overflow-hidden">
          {/* Left panel - Service details */}
          <div className="bg-gradient-to-b from-primary-50 to-primary-100 p-6 sm:p-8 flex flex-col gap-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-secondary-900 leading-tight mb-4">
                {service.title}
              </h2>
              <p className="text-secondary-600 leading-relaxed text-sm sm:text-base">
                {service.description}
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-semibold text-secondary-900">Total Cost:</span>
                <span className="text-3xl font-bold text-primary-600">$5,000 USD</span>
              </div>
              <p className="text-xs text-secondary-500">One-time payment • Lifetime access</p>
            </div>

            <div>
              <h3 className="font-semibold text-secondary-900 mb-3">What you'll get:</h3>
              <ul className="space-y-2">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-secondary-600">
                    <span className="text-primary-600 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-primary-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-primary-700">
                <i className="bi bi-shield-check text-lg"></i>
                <span className="font-semibold text-sm">Secure Payment Processing</span>
              </div>
              <p className="text-xs text-primary-600 mt-1">256-bit SSL encryption • PCI compliant</p>
            </div>
          </div>

          {/* Right panel - Payment form */}
          <div className="bg-secondary-50 p-6 sm:p-8">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-secondary-900 mb-2">Complete Your Purchase</h3>
              <p className="text-secondary-600 text-sm">Fill in your details to access this premium strategy guide.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <i className="bi bi-person text-secondary-400 absolute left-3 top-1/2 -translate-y-1/2"></i>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Full Name*"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-secondary-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400"
                />
              </div>

              <div className="relative">
                <i className="bi bi-envelope text-secondary-400 absolute left-3 top-1/2 -translate-y-1/2"></i>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Email Address*"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-secondary-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400"
                />
              </div>

              <div className="relative">
                <i className="bi bi-telephone text-secondary-400 absolute left-3 top-1/2 -translate-y-1/2"></i>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Phone Number*"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-secondary-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400"
                />
              </div>

              <div className="relative">
                <i className="bi bi-geo-alt text-secondary-400 absolute left-3 top-1/2 -translate-y-1/2"></i>
                <input
                  type="text"
                  name="county"
                  value={formData.county}
                  onChange={handleInputChange}
                  required
                  placeholder="County/Constituency*"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-white border border-secondary-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-secondary-900 placeholder-secondary-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Payment Method</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={handleInputChange}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-secondary-700">Credit/Debit Card</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="paypal"
                      checked={formData.paymentMethod === 'paypal'}
                      onChange={handleInputChange}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-secondary-700">PayPal</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bank"
                      checked={formData.paymentMethod === 'bank'}
                      onChange={handleInputChange}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-secondary-700">Bank Transfer</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <i className="bi bi-arrow-repeat animate-spin mr-2"></i>
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <i className="bi bi-credit-card mr-2"></i>
                    Pay $5,000 USD & Get Access
                  </>
                )}
              </button>

              <p className="text-xs text-center text-secondary-500">
                Secure payment • Instant access • 30-day money-back guarantee
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
import {
  RocketIcon,
  TrophyIcon,
  ChartIcon,
  PeopleIcon,
  LightbulbIcon,
  GlobeIcon,
  CheckCircleIcon,
  StarIcon
} from '../components/AnimatedIcons';

const Home = () => {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const openConsultationModal = () => {
    setIsConsultationModalOpen(true);
  };

  const closeConsultationModal = () => {
    setIsConsultationModalOpen(false);
  };

  const openPurchaseModal = (service) => {
    setSelectedService(service);
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
    setSelectedService(null);
  };


  const services = [
    {
      title: "How to Devise a Winning Campaign Strategy",
      description: "Master the fundamentals of creating a winning campaign strategy that adapts to Kenya's unique political landscape and voter dynamics.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Strategic Planning", "Campaign Design", "Risk Assessment", "Victory Roadmap"]
    },
    {
      title: "How to Organise Your Campaign",
      description: "Learn the essential skills for organizing your campaign effectively for maximum impact and operational efficiency.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Team Structure", "Resource Management", "Timeline Planning", "Operations Excellence"]
    },
    {
      title: "How to Create Effective Campaign Advertising",
      description: "Discover how to create powerful and persuasive campaign advertising that resonates with Kenyan voters and drives engagement.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Message Development", "Creative Strategy", "Media Planning", "Brand Positioning"]
    },
    {
      title: "How to Use Domestic Policy to Electoral Advantage",
      description: "Learn strategic techniques to leverage domestic policy issues for electoral success and effective voter mobilization.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Policy Analysis", "Issue Framing", "Voter Priorities", "Position Development"]
    },
    {
      title: "How to Use Foreign Policy to Electoral Advantage",
      description: "Master the strategic use of foreign policy to enhance your electoral positioning and build credibility with voters.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["International Relations", "Diplomatic Strategy", "Global Positioning", "Policy Communication"]
    },
    {
      title: "How to Use Opinion Polls and Focus Groups",
      description: "Strategic application of opinion polls and focus groups to guide campaign decisions and optimize messaging strategies.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Poll Analysis", "Focus Group Design", "Data Interpretation", "Strategic Insights"]
    },
    {
      title: "How to Perform Well in Media Interviews",
      description: "Develop the skills to perform confidently and effectively in media interviews with professional training and preparation.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Interview Techniques", "Crisis Management", "Message Delivery", "Media Strategy"]
    },
    {
      title: "How to Give Effective Speeches",
      description: "Learn to deliver impactful and memorable speeches that inspire voters and build electoral momentum.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Speech Writing", "Delivery Techniques", "Audience Engagement", "Rhetorical Skills"]
    },
    {
      title: "How to Survive Your Opponent's Attacks",
      description: "Master techniques to survive and counter your opponent's attacks while maintaining voter trust and campaign momentum.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Attack Preparation", "Response Strategy", "Crisis Communication", "Damage Control"]
    },
    {
      title: "How to Identify Your Voters and Get them to the Polls",
      description: "Strategic voter identification and mobilization techniques to ensure maximum turnout on election day.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      features: ["Voter Targeting", "Mobilization Strategy", "GOTV Tactics", "Election Day Operations"]
    }
  ];

  const stats = [
    { number: 10, label: "Keys to Victory", suffix: "" },
    { number: 1000, label: "Strategic Leaders Trained", suffix: "+" },
    { number: 47, label: "Counties Covered", suffix: "" },
    { number: 85, label: "Election Success Rate", suffix: "%" }
  ];

  const caseStudies = [
    {
      title: "Presidential Campaign Success",
      description: "Strategic application of the 10 Keys to Victory™ program led to a decisive presidential victory through comprehensive campaign strategy.",
      result: "58% National Vote",
      region: "National Level",
      icon: "bi-trophy"
    },
    {
      title: "Gubernatorial Strategy Triumph",
      description: "Masterclass-trained candidate overcame regional challenges using advanced policy positioning and voter mobilization techniques.",
      result: "52% County Vote",
      region: "County Level",
      icon: "bi-bar-chart"
    },
    {
      title: "Parliamentary Masterclass Graduate",
      description: "Strategy101 program participant successfully defended parliamentary seat using opponent attack survival and GOTV strategies.",
      result: "Re-election Victory",
      region: "Constituency Level",
      icon: "bi-shield"
    }
  ];

  const testimonials = [
    {
      quote: "The 10 Keys to Victory™ program from KISS completely transformed my understanding of strategic campaigning. The masterclasses provided the framework for our successful presidential campaign.",
      name: "Hon. Dr. Samuel Kiprono",
      position: "Former Presidential Candidate",
      region: "National Leadership"
    },
    {
      quote: "Strategy101 gave me the tools to organize and execute a winning gubernatorial campaign. The partnership with Democracy Institute brought unparalleled strategic depth to our efforts.",
      name: "Hon. Margaret Wanjiku",
      position: "County Governor",
      region: "Strategic Leadership Graduate"
    },
    {
      quote: "The DI-KISS masterclasses on policy development and media training were instrumental in my parliamentary victory. Their strategic approach to opponent attacks saved my campaign.",
      name: "Hon. James Kiprop",
      position: "Member of Parliament",
      region: "Parliamentary Strategy Graduate"
    }
  ];

  const achievements = [
    { icon: "bi-trophy", title: "10 Keys to Victory™", description: "Comprehensive strategic framework for electoral success" },
    { icon: "bi-graph-up", title: "Strategy101 Program", description: "Advanced campaign strategy training for leaders" },
    { icon: "bi-globe", title: "Kenya Democracy Project", description: "Research-driven insights for democratic processes" },
    { icon: "bi-lightbulb", title: "DI-KISS Partnership", description: "Collaborative excellence in strategic studies" },
    { icon: "bi-handshake", title: "iTarget Media EA", description: "Digital strategy integration for modern campaigns" },
    { icon: "bi-bullseye", title: "Strategic Foundation", description: "Strategy as the foundation of all success" }
  ];


  return (
    <div>
      {/* Static Hero Section */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-0 pb-0">
        <div className="max-w-screen-2xl mx-auto">
          {/* Hero Card Container */}
          <div className="bg-white rounded-3xl shadow-2xl border border-secondary-200 overflow-hidden hover:shadow-3xl transition-shadow duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Side - Text Content */}
              <div className="p-8 sm:p-12 lg:p-14 xl:p-16 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-6xl font-bold text-secondary-900 mb-6 sm:mb-8 leading-tight">
                  Strategy Is the Foundation of Success
                </h1>
                <p className="text-base sm:text-lg lg:text-xl xl:text-xl text-secondary-600 mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  Master the 10 Keys to Victory™ with Kehosa Institute for Strategic Studies (KISS) in partnership with Democracy Institute (DI). Transform your political ambitions into guaranteed electoral triumphs.
                </p>
                 {/* Right Side - Stats Cards */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 mt-8">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-secondary-100"
                >
                  <div className="text-left">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-600 mb-1 sm:mb-2">
                      <AnimatedCounter
                        end={stat.number}
                        suffix={stat.suffix}
                        duration={2500}
                      />
                    </div>
                    <div className="text-secondary-700 font-semibold text-xs sm:text-sm lg:text-base mb-2 sm:mb-3">{stat.label}</div>
                    <div className="w-12 sm:w-16 h-1 bg-primary-500 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link to="/services" className="btn-primary transform hover:scale-105 transition-transform duration-200 text-sm sm:text-base py-4 px-8 sm:px-10">
                    Begin Your Strategic Journey
                  </Link>
                  <Link to="/about" className="btn-secondary transform hover:scale-105 transition-transform duration-200 text-sm sm:text-base py-4 px-8 sm:px-10">
                    Learn More About KISS
                  </Link>
                  
                </div>
              </div>

              {/* Right Side - Image */}
              <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50">
                <div className="relative h-full min-h-[320px] lg:min-h-[360px] xl:min-h-[400px] p-10">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-500 h-full">
                    <div className="aspect-w-16 aspect-h-12 bg-gradient-to-br from-primary-600 to-primary-800 h-full">
                      <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                      <img
                        src="https://res.cloudinary.com/dzj1fusx1/image/upload/v1768651594/26636638-0609-4a28-87a3-c14ac5244ea2.png"
                        alt="Strategic political planning and success"
                        className="w-full h-full object-cover lg:object-contain object-center"
                      />
                      
                    </div>
                  </div>

                  {/* Floating elements for visual interest */}
                  <div className="absolute -top-3 -left-3 w-20 h-20 bg-primary-500 rounded-full opacity-20 animate-pulse"></div>
                  <div className="absolute -bottom-5 -right-5 w-16 h-16 bg-accent-400 rounded-full opacity-30 animate-pulse" style={{animationDelay: '1s'}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Enhanced Services Overview */}
      <section className="bg-secondary-50 pt-8 pb-12 sm:pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-pattern opacity-5"></div>
        <div className="relative container-max">
          <div className="text-center mb-6 sm:mb-8 lg:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-secondary-900 mb-4 sm:mb-6">
              Comprehensive Campaign Services
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-secondary-600 max-w-4xl mx-auto leading-relaxed px-4">
              From strategic planning to execution, we provide end-to-end support for political campaigns
              that understand Kenya's diverse electorate, regional dynamics, and modern campaigning techniques.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-3 sm:p-4 lg:p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group animate-fade-in flex flex-col h-full cursor-pointer"
                style={{animationDelay: `${index * 0.1}s`}}
                onClick={() => openPurchaseModal(service)}
              >
                <div className="flex-grow">
                  <div className="mb-2 sm:mb-3 lg:mb-4 overflow-hidden rounded-md">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-24 sm:h-28 lg:h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-secondary-900 mb-1 sm:mb-2 group-hover:text-primary-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-secondary-600 leading-relaxed mb-2 sm:mb-3 text-xs sm:text-sm">{service.description}</p>
                  <div className="space-y-1">
                    {service.features.slice(0, 2).map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-xs text-secondary-500">
                        <span className="text-primary-600 mr-1">✓</span>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-auto">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openPurchaseModal(service);
                    }}
                    className="w-full bg-primary-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 text-xs sm:text-sm"
                  >
                    Purchase Now - $5000
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link to="/services" className="btn-primary transform hover:scale-105 transition-transform duration-200 text-sm sm:text-base py-2.5 sm:py-3 px-4 sm:px-6">
                View All Services
              </Link>
              <Link to="/resources" className="btn-secondary transform hover:scale-105 transition-transform duration-200 text-sm sm:text-base py-2.5 sm:py-3 px-4 sm:px-6">
                Download Free Resources
              </Link>
            </div>
            <p className="text-secondary-500 text-sm sm:text-base lg:text-lg px-4">
              Trusted by political leaders across all 47 counties
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="bg-white  ">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Campaign Success Stories
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Real results from campaigns that implemented our strategic methodologies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-secondary-50 to-primary-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 animate-fade-in"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className="text-6xl mb-4 text-primary-600">
                  {study.icon === 'bi-trophy' && <TrophyIcon className="text-6xl" />}
                  {study.icon === 'bi-bar-chart' && <ChartIcon className="text-6xl" />}
                  {study.icon === 'bi-phone' && <GlobeIcon className="text-6xl" />}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3">{study.title}</h3>
                <p className="text-secondary-600 mb-4 leading-relaxed">{study.description}</p>
                <div className="border-t border-secondary-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-primary-600 font-semibold">{study.result}</span>
                    <span className="text-secondary-500 text-sm">{study.region}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/resources" className="btn-primary">
              Read More Case Studies
            </Link>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="bg-gradient-to-r from-secondary-900 to-primary-900 text-white section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="relative container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why We're Kenya's Leading Choice
            </h2>
            <p className="text-xl text-secondary-300 max-w-3xl mx-auto">
              Excellence, innovation, and results that speak for themselves
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="bg-white bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105 animate-fade-in"
                style={{animationDelay: `${index * 0.15}s`}}
              >
                <div className="text-6xl mb-4 text-primary-600">
                  {achievement.icon === 'bi-trophy' && <TrophyIcon className="text-6xl" />}
                  {achievement.icon === 'bi-graph-up' && <ChartIcon className="text-6xl" />}
                  {achievement.icon === 'bi-globe' && <GlobeIcon className="text-6xl" />}
                  {achievement.icon === 'bi-lightbulb' && <LightbulbIcon className="text-6xl" />}
                  {achievement.icon === 'bi-handshake' && <PeopleIcon className="text-6xl" />}
                  {achievement.icon === 'bi-bullseye' && <RocketIcon className="text-6xl" />}
                </div>
                <h3 className="text-xl font-bold mb-3">{achievement.title}</h3>
                <p className="text-secondary-200 leading-relaxed">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Why Choose Us */}
      <section className="bg-white section-padding relative">
        <div className="absolute inset-0 bg-pattern opacity-5"></div>
        <div className="relative container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-6xl font-bold text-secondary-900 mb-8">
                Why Political Leaders Choose Us
              </h2>
              <div className="space-y-8">
                <div className="flex items-start space-x-6 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-xl">✓</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">Proven Track Record</h4>
                    <p className="text-secondary-600 leading-relaxed">20+ years of successful campaign management across Kenya's political landscape, with an 85% success rate.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-xl">✓</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">Data-Driven Approach</h4>
                    <p className="text-secondary-600 leading-relaxed">Advanced analytics, AI-powered voter insights, and proprietary algorithms for maximum campaign effectiveness.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-xl">✓</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">Ethical Campaigning</h4>
                    <p className="text-secondary-600 leading-relaxed">Committed to transparent, inclusive, and ethical political engagement that strengthens democracy.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-6 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-xl">✓</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">Local Expertise</h4>
                    <p className="text-secondary-600 leading-relaxed">Deep understanding of Kenyan politics, regional dynamics, cultural nuances, and voter psychology across all 47 counties.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-10 rounded-3xl shadow-2xl overflow-hidden">
              {/* Background Image */}
              <div className="absolute inset-0 opacity-10">
                <img
                  src="https://images.unsplash.com/photo-1555848962-6e79363ec58f?w=600&h=400&fit=crop"
                  alt="Election victory celebration"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="relative text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <i className="bi bi-trophy text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-secondary-900 mb-4">Ready to Win Your Election?</h3>
              </div>

              <p className="text-secondary-600 mb-8 text-lg leading-relaxed">
                Get expert guidance from Kenya's leading political strategists. Our consultation process
                includes comprehensive campaign assessment, customized strategy development, and ongoing support.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3 text-secondary-700">
                  <span className="text-primary-600 text-xl">✓</span>
                  <span>Free initial consultation</span>
                </div>
                <div className="flex items-center space-x-3 text-secondary-700">
                  <span className="text-primary-600 text-xl">✓</span>
                  <span>Custom campaign assessment</span>
                </div>
                <div className="flex items-center space-x-3 text-secondary-700">
                  <span className="text-primary-600 text-xl">✓</span>
                  <span>Strategic recommendations</span>
                </div>
                <div className="flex items-center space-x-3 text-secondary-700">
                  <span className="text-primary-600 text-xl">✓</span>
                  <span>24/7 campaign support</span>
                </div>
              </div>

              <div className="space-y-4">
                <Link to="/services" className="btn-primary w-full text-center block transform hover:scale-105 transition-transform duration-200">
                  Get Expert Guidance
                </Link>
                <Link to="/resources" className="btn-secondary w-full text-center block transform hover:scale-105 transition-transform duration-200">
                  Download Free Resources
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section className="bg-gradient-to-r from-secondary-50 to-primary-50 py-16">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Trusted by Political Leaders Nationwide
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Join hundreds of successful politicians who have transformed their campaigns with our expertise
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">500+</div>
              <div className="text-secondary-600">Successful Campaigns</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">47</div>
              <div className="text-secondary-600">Counties Served</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">95%</div>
              <div className="text-secondary-600">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">24/7</div>
              <div className="text-secondary-600">Support Available</div>
            </div>
          </div>

          {/* Client logos placeholder */}
          <div className="flex flex-wrap justify-center items-center space-x-8 opacity-60">
            <div className="text-secondary-500 font-semibold">Featured in:</div>
            <div className="text-secondary-400">Nation Media Group</div>
            <div className="text-secondary-400">KTN News</div>
            <div className="text-secondary-400">The Standard</div>
            <div className="text-secondary-400">Citizen TV</div>
          </div>
        </div>
      </section>

      {/* Creative CTA Section */}
      <section className="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative container-max">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-6xl mb-6 animate-float">
              <i className="bi bi-bullseye"></i>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Your Winning Strategy Awaits
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-primary-100 leading-relaxed">
              Don't let your political aspirations become just another campaign.
              Transform them into victory stories with our proven methodologies.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <div className="text-3xl mb-4">📞</div>
                <h3 className="text-xl font-bold mb-2">Free Consultation</h3>
                <p className="text-primary-100 mb-4">30-minute strategy session at no cost</p>
                <button
                  onClick={openConsultationModal}
                  className="bg-white text-primary-600 px-6 py-2 rounded-lg font-semibold hover:bg-secondary-50 transition-colors duration-200"
                >
                  Book Now
                </button>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <div className="text-3xl mb-4">
                  <i className="bi bi-bar-chart"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Campaign Audit</h3>
                <p className="text-primary-100 mb-4">Free analysis of your current strategy</p>
                <button className="bg-white text-primary-600 px-6 py-2 rounded-lg font-semibold hover:bg-secondary-50 transition-colors duration-200">
                  Get Audit
                </button>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <div className="text-3xl mb-4">
                  <i className="bi bi-rocket"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Success Stories</h3>
                <p className="text-primary-100 mb-4">See how we helped others win</p>
                <button className="bg-white text-primary-600 px-6 py-2 rounded-lg font-semibold hover:bg-secondary-50 transition-colors duration-200">
                  View Stories
                </button>
              </div>
            </div>

            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Winning?</h3>
              <p className="text-primary-100 mb-6">
                Join the ranks of successful political leaders who chose strategy over chance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <input
                  type="email"
                  placeholder="Enter your email for exclusive insights"
                  className="flex-1 px-4 py-3 rounded-lg text-secondary-900 focus:outline-none focus:ring-2 focus:ring-white"
                />
                <button className="bg-accent-500 hover:bg-accent-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200 transform hover:scale-105">
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialCarousel testimonials={testimonials} />

      {/* Interactive Process Overview */}
      <section className="bg-secondary-50 section-padding relative">
        <div className="absolute inset-0 bg-pattern opacity-5"></div>
        <div className="relative container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Our Winning Process
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              A systematic, data-driven approach that transforms political aspirations into guaranteed victories
            </p>
          </div>

          <div className="relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-600"></div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {[
                {
                  step: "01",
                  title: "Discovery & Assessment",
                  description: "Comprehensive analysis of your political landscape, goals, and challenges.",
                  icon: "bi-search",
                  color: "from-blue-500 to-blue-600"
                },
                {
                  step: "02",
                  title: "Strategy Development",
                  description: "Custom campaign strategy based on data analysis and voter insights.",
                  icon: "bi-clipboard-check",
                  color: "from-green-500 to-green-600"
                },
                {
                  step: "03",
                  title: "Implementation",
                  description: "Execution of campaign strategies with continuous monitoring and optimization.",
                  icon: "bi-lightning-charge",
                  color: "from-yellow-500 to-yellow-600"
                },
                {
                  step: "04",
                  title: "Monitoring & Adjustment",
                  description: "Real-time campaign performance tracking and strategic adjustments.",
                  icon: "bi-bar-chart-line",
                  color: "from-purple-500 to-purple-600"
                },
                {
                  step: "05",
                  title: "Victory & Analysis",
                  description: "Election day support and comprehensive post-election analysis.",
                  icon: "bi-trophy",
                  color: "from-red-500 to-red-600"
                }
              ].map((step, index) => (
                <div key={index} className="text-center relative">
                  <div className={`w-20 h-20 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    {step.icon === 'bi-search' && <CheckCircleIcon className="text-2xl text-white" />}
                    {step.icon === 'bi-clipboard-check' && <ChartIcon className="text-2xl text-white" />}
                    {step.icon === 'bi-lightning-charge' && <LightbulbIcon className="text-2xl text-white" />}
                    {step.icon === 'bi-bar-chart-line' && <GlobeIcon className="text-2xl text-white" />}
                    {step.icon === 'bi-trophy' && <TrophyIcon className="text-2xl text-white" />}
                  </div>

                  <div className="bg-white shadow-md rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 font-bold text-lg text-primary-600 border-2 border-primary-100">
                    {step.step}
                  </div>

                  <h3 className="text-xl font-bold mb-3 text-secondary-900">
                    {step.title}
                  </h3>
                  <p className="text-secondary-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Interactive CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 max-w-2xl mx-auto border border-primary-100">
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                Ready to Follow This Process?
              </h3>
              <p className="text-secondary-600 mb-6">
                Start your journey to electoral success with our proven 5-step methodology
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Start Your Assessment
                  <i className="bi bi-arrow-right ml-2"></i>
                </button>
                <button className="bg-white hover:bg-secondary-50 text-primary-600 border border-primary-600 px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fun Interactive Section */}
      <section className="bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="relative container-max">
          <div className="text-center mb-16">
            <div className="text-6xl mb-4 animate-bounce">
              <i className="bi bi-bullseye"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Why Choose Kehosa Election Consultancy Agency?
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Discover what makes us Kenya's most trusted political strategy partner
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:rotate-1">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="bi bi-trophy text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3">Proven Track Record</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">20+ years of experience</span>
                </div>
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">500+ successful campaigns</span>
                </div>
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">95% client satisfaction</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:-rotate-1">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="bi bi-book text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3">Strategy101 Program</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">Comprehensive campaign strategy</span>
                </div>
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">DI-KISS masterclass access</span>
                </div>
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">Victory roadmap development</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:rotate-1">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="bi bi-building text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3">Kenya Democracy Project</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">All 47 counties covered</span>
                </div>
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">Democratic research & analysis</span>
                </div>
                <div className="flex items-center">
                  <i className="bi bi-check-circle text-green-600 mr-3"></i>
                  <span className="text-secondary-700">Leadership development focus</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fun call-to-action */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-8 text-white max-w-2xl mx-auto shadow-2xl">
              <div className="text-5xl mb-4 animate-bounce">
                <i className="bi bi-stars"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">
                "Strategy Is the Foundation of Success"
              </h3>
              <p className="text-yellow-100 mb-6">
                Join KISS and discover how strategic thinking transforms political campaigns into victories!
              </p>
              <button className="bg-white text-orange-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-orange-50 transition-all duration-300 transform hover:scale-105 shadow-lg animate-pulse">
                <i className="bi bi-rocket mr-2"></i>Register for Strategy101
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 text-white section-padding">
        <div className="container-max text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            "We Are Ready and Open for Business — Are You?"
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-primary-100">
            Join the ranks of strategic leaders who have mastered the 10 Keys to Victory™.
            Transform your political ambitions into electoral triumphs with KISS.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-white text-primary-600 hover:bg-secondary-50">
              Register for Strategy101
            </Link>
            <a href="tel:+254722655911" className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-primary-600">
              Call +254 722 655911
            </a>
          </div>
        </div>
      </section>

      {/* Consultation Modal */}
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={closeConsultationModal}
      />

      {/* Purchase Modal */}
      <PurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        service={selectedService}
      />

    </div>
  );
};

export default Home;
